/**
 * Liquid Glass SVG Displacement Map Generator
 * Based on: https://kube.io/blog/liquid-glass-css-svg/
 *
 * Implements physics-based refraction using Snell's Law to create
 * realistic glass distortion effects.
 */

export type SurfaceProfile = 'convex' | 'concave' | 'squircle' | 'lip'

interface DisplacementOptions {
  profile?: SurfaceProfile
  refractiveIndex?: number
  size?: number
  samples?: number
}

/**
 * Calculate surface height based on profile type
 * Uses mathematical curves to simulate different glass shapes
 */
function calculateSurfaceHeight(
  r: number, // normalized radius (0-1)
  profile: SurfaceProfile
): number {
  switch (profile) {
    case 'convex':
      // Simple circular arc
      return Math.sqrt(Math.max(0, 1 - r * r))

    case 'squircle':
      // Apple's preferred smoother transition
      // Uses superellipse formula: x^n + y^n = 1
      const n = 4 // Squircle exponent
      return Math.pow(Math.max(0, 1 - Math.pow(r, n)), 1 / n)

    case 'concave':
      // Inverted bowl shape
      return 1 - Math.sqrt(Math.max(0, 1 - r * r))

    case 'lip':
      // Blended convex/concave for raised rim
      const lipWidth = 0.15
      if (r < lipWidth) {
        // Outer convex rim
        const localR = r / lipWidth
        return Math.sqrt(Math.max(0, 1 - localR * localR)) * 0.3
      } else {
        // Inner concave
        const localR = (r - lipWidth) / (1 - lipWidth)
        return 0.3 - Math.sqrt(Math.max(0, 1 - localR * localR)) * 0.2
      }

    default:
      return 0
  }
}

/**
 * Calculate surface normal (derivative) for refraction angle
 */
function calculateSurfaceNormal(
  r: number,
  profile: SurfaceProfile,
  delta: number = 0.001
): number {
  const h1 = calculateSurfaceHeight(r - delta, profile)
  const h2 = calculateSurfaceHeight(r + delta, profile)
  return (h2 - h1) / (2 * delta)
}

/**
 * Apply Snell's Law to calculate refraction
 * n1 * sin(θ1) = n2 * sin(θ2)
 */
function calculateRefraction(
  incidentAngle: number,
  n1: number = 1.0, // Air
  n2: number = 1.5  // Glass
): number {
  const sinTheta1 = Math.sin(incidentAngle)
  const sinTheta2 = (n1 / n2) * sinTheta1

  // Check for total internal reflection
  if (Math.abs(sinTheta2) > 1) {
    return incidentAngle // No refraction
  }

  return Math.asin(sinTheta2)
}

/**
 * Generate displacement vector for a given radius
 */
function generateDisplacementVector(
  r: number,
  profile: SurfaceProfile,
  refractiveIndex: number
): { x: number; y: number; magnitude: number } {
  if (r === 0) {
    return { x: 0, y: 0, magnitude: 0 }
  }

  // Calculate surface normal angle
  const derivative = calculateSurfaceNormal(r, profile)
  const surfaceAngle = Math.atan(derivative)

  // Incident ray is orthogonal (straight down)
  const incidentAngle = Math.PI / 2 - surfaceAngle

  // Apply Snell's Law
  const refractedAngle = calculateRefraction(incidentAngle, 1.0, refractiveIndex)

  // Calculate displacement magnitude
  const angleChange = refractedAngle - incidentAngle
  const displacement = Math.tan(angleChange) * calculateSurfaceHeight(r, profile)

  return {
    x: displacement,
    y: 0, // Radial symmetry - will be rotated
    magnitude: Math.abs(displacement)
  }
}

/**
 * Generate complete displacement map data URL
 */
export function generateDisplacementMap(
  options: DisplacementOptions = {}
): { dataUrl: string; maxDisplacement: number } {
  const {
    profile = 'squircle',
    refractiveIndex = 1.5,
    size = 256,
    samples = 127
  } = options

  // Calculate displacement vectors for each radius sample
  const displacements: Array<{ x: number; y: number; magnitude: number }> = []
  let maxMagnitude = 0

  for (let i = 0; i < samples; i++) {
    const r = i / (samples - 1) // Normalize to 0-1
    const displacement = generateDisplacementVector(r, profile, refractiveIndex)
    displacements.push(displacement)
    maxMagnitude = Math.max(maxMagnitude, displacement.magnitude)
  }

  // Create canvas for displacement map
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!

  const imageData = ctx.createImageData(size, size)
  const data = imageData.data

  const centerX = size / 2
  const centerY = size / 2
  const maxRadius = size / 2

  // Generate displacement map using radial symmetry
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - centerX
      const dy = y - centerY
      const r = Math.sqrt(dx * dx + dy * dy) / maxRadius
      const angle = Math.atan2(dy, dx)

      const idx = (y * size + x) * 4

      if (r <= 1) {
        // Interpolate displacement from samples
        const sampleIndex = r * (samples - 1)
        const i1 = Math.floor(sampleIndex)
        const i2 = Math.min(i1 + 1, samples - 1)
        const t = sampleIndex - i1

        const d1 = displacements[i1]
        const d2 = displacements[i2]

        // Lerp displacement magnitude
        const magnitude = d1.magnitude + (d2.magnitude - d1.magnitude) * t

        // Rotate displacement vector by angle
        const displacementX = magnitude * Math.cos(angle)
        const displacementY = magnitude * Math.sin(angle)

        // Normalize to 0-1 range
        const normalizedX = maxMagnitude > 0 ? displacementX / maxMagnitude : 0
        const normalizedY = maxMagnitude > 0 ? displacementY / maxMagnitude : 0

        // Map to 0-255 with 128 as neutral
        data[idx] = Math.round(128 + normalizedX * 127)     // R = X displacement
        data[idx + 1] = Math.round(128 + normalizedY * 127) // G = Y displacement
        data[idx + 2] = 128                                  // B = unused
        data[idx + 3] = 255                                  // A = opaque
      } else {
        // Outside circle - no displacement
        data[idx] = 128
        data[idx + 1] = 128
        data[idx + 2] = 128
        data[idx + 3] = 255
      }
    }
  }

  ctx.putImageData(imageData, 0, 0)

  return {
    dataUrl: canvas.toDataURL('image/png'),
    maxDisplacement: maxMagnitude * size * 0.4 // Increased scale for dramatic visible distortion effect
  }
}

/**
 * Generate specular highlight gradient
 * Adds rim lighting for realistic glass edges
 */
export function generateSpecularHighlight(
  size: number = 256,
  intensity: number = 0.6
): string {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!

  const centerX = size / 2
  const centerY = size / 2
  const radius = size / 2

  // Create radial gradient for rim lighting - tighter gradient for more visible edge highlight
  const gradient = ctx.createRadialGradient(
    centerX, centerY, radius * 0.6,
    centerX, centerY, radius
  )

  gradient.addColorStop(0, `rgba(255, 255, 255, 0)`)
  gradient.addColorStop(0.7, `rgba(255, 255, 255, 0)`)
  gradient.addColorStop(0.85, `rgba(255, 255, 255, ${intensity * 0.4})`)
  gradient.addColorStop(0.95, `rgba(255, 255, 255, ${intensity * 0.7})`)
  gradient.addColorStop(1, `rgba(255, 255, 255, ${intensity})`)

  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, size, size)

  return canvas.toDataURL('image/png')
}
